// Telegram WebApp SDK-обвязка (порт docs/explore/tg-miniapp/tg.js на TS).
// Всё в try/catch: в обычном браузере SDK создаёт заглушку, поэтому переключаемся
// на нативные контролы только внутри реального Telegram WebView (`inTelegram`).

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type NotifyType = 'error' | 'success' | 'warning';

interface MainButtonParams {
  text?: string;
  color?: string;
  text_color?: string;
  is_active?: boolean;
  is_visible?: boolean;
}

interface ThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
}

interface TgWebApp {
  initData?: string;
  platform?: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: ThemeParams;
  initDataUnsafe?: { user?: { id?: number; language_code?: string }; start_param?: string };
  ready?: () => void;
  expand?: () => void;
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
  setBottomBarColor?: (c: string) => void;
  onEvent?: (event: string, cb: () => void) => void;
  showAlert?: (m: string) => void;
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  BackButton?: { show: () => void; hide: () => void; onClick: (cb: () => void) => void; offClick: (cb: () => void) => void };
  MainButton?: {
    setParams: (p: MainButtonParams) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  HapticFeedback?: {
    impactOccurred: (s: HapticStyle) => void;
    notificationOccurred: (t: NotifyType) => void;
  };
  version?: string;
  isVersionAtLeast?: (v: string) => boolean;
  isFullscreen?: boolean;
  requestFullscreen?: () => void;
  addToHomeScreen?: () => void;
  checkHomeScreenStatus?: (cb: (status: string) => void) => void;
  CloudStorage?: {
    getItem: (key: string, cb: (error: string | null, value?: string) => void) => void;
    setItem: (key: string, value: string, cb: (error: string | null, ok?: boolean) => void) => void;
    removeItem: (key: string, cb: (error: string | null, ok?: boolean) => void) => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

const tg: TgWebApp | undefined = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

export const inTelegram = Boolean(tg && (tg.initData || (tg.platform && tg.platform !== 'unknown')));

function safely(run: () => void): void {
  try {
    run();
  } catch (error) {
    console.warn('Telegram WebApp action unavailable:', error);
  }
}

export function haptic(style: HapticStyle = 'light'): void {
  safely(() => tg?.HapticFeedback?.impactOccurred(style));
}

export function notify(type: NotifyType = 'success'): void {
  safely(() => tg?.HapticFeedback?.notificationOccurred(type));
}

/** language_code из initDataUnsafe (первичный источник языка). */
export function tgLanguage(): string | null {
  return tg?.initDataUnsafe?.user?.language_code ?? null;
}

/** Стабильный ключ пользователя для префилла ника (tg id или 'anon'). */
export function tgUserKey(): string {
  const id = tg?.initDataUnsafe?.user?.id;
  return id != null ? String(id) : 'anon';
}

/** CloudStorage 6.9+; null вне Telegram — сервисы используют localStorage. */
export function cloudStorage(): TgWebApp['CloudStorage'] | null {
  return tg?.isVersionAtLeast?.('6.9') && tg.CloudStorage ? tg.CloudStorage : null;
}

/**
 * Параметр диплинка: initDataUnsafe.start_param (нативный Telegram) с фолбэком на
 * tgWebAppStartParam из URL (веб-версия Mini App). null — обычный запуск.
 */
export function startParam(): string | null {
  const p = tg?.initDataUnsafe?.start_param;
  if (p) return p;
  try {
    return new URLSearchParams(window.location.search).get('tgWebAppStartParam');
  } catch {
    return null;
  }
}

export function alertMsg(message: string): void {
  if (inTelegram && tg?.showAlert) safely(() => tg.showAlert!(message));
  else window.alert(message);
}

export function openLink(url: string): void {
  if (inTelegram && tg?.openLink) safely(() => tg.openLink!(url));
  else window.open(url, '_blank', 'noopener');
}

/** t.me-ссылка через нативный переход Telegram (внутри WebView), иначе — новая вкладка. */
export function openTelegramLink(url: string): void {
  if (inTelegram && tg?.openTelegramLink) safely(() => tg.openTelegramLink!(url));
  else window.open(url, '_blank', 'noopener');
}

/**
 * Поделиться текстом+ссылкой. В Telegram — нативный шэринг через t.me/share/url
 * (openTelegramLink подтверждён в telegram-web-app.js). В браузере — navigator.share,
 * иначе копирование в буфер. Возвращает 'copied', когда упали в буфер (вызывающий
 * показывает тост), иначе 'shared'.
 */
export async function shareText(text: string, link: string): Promise<'shared' | 'copied'> {
  if (inTelegram) {
    openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`);
    return 'shared';
  }
  const full = `${text} ${link}`;
  const nav = navigator as Navigator & { share?: (data: { text: string }) => Promise<void> };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ text: full });
      return 'shared';
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return 'shared'; // пользователь отменил
      /* иначе — падаем в копирование */
    }
  }
  try {
    await navigator.clipboard?.writeText(full);
  } catch {
    /* буфер может быть заблокирован в webview — тост всё равно покажем */
  }
  return 'copied';
}

// Бренд-фон Mini App (BRAND.md) — приложение ВСЕГДА тёмное.
const BRAND_BG = '#12141d';

/**
 * Согласуем нативный хром Telegram (header/background/bottom bar) с нашим тёмным UI.
 * Чек-лист листинга: бренд остаётся тёмным, но в ТЁМНОЙ теме Telegram цвета берём из
 * themeParams (нативный хедер сливается с системным тёмным); в СВЕТЛОЙ теме держим наш
 * фирменный тёмный — тёмный UI на светлом системном хроме допустим, контент не ломается.
 */
function applyThemeColors(): void {
  const dark = tg?.colorScheme !== 'light';
  const bg = (dark && tg?.themeParams?.bg_color) || BRAND_BG;
  safely(() => tg?.setHeaderColor?.(bg));
  safely(() => tg?.setBackgroundColor?.(bg));
  safely(() => tg?.setBottomBarColor?.(bg));
}

/** Инициализация: ready/expand + согласование хрома + подписка на themeChanged. */
/** Полный экран (Bot API 8.0+): убирает шапку Telegram на мобильных. */
function requestFullscreenSafe(): void {
  if (!inTelegram || !tg?.requestFullscreen) return;
  if (!tg.isVersionAtLeast?.('8.0')) return;
  const p = tg.platform ?? '';
  if (p !== 'ios' && p !== 'android') return; // на десктопе фуллскрин-окно не нужно
  safely(() => tg!.requestFullscreen!());
  // при фуллскрине системные часы/чёлка перекрывают верх — включаем класс,
  // стили возьмут отступ из --tg-*-safe-area-inset (Telegram проставляет CSS-переменные)
  document.documentElement.classList.add('tg-fullscreen');
  safely(() => tg!.onEvent?.('fullscreenChanged', () => {
    document.documentElement.classList.toggle('tg-fullscreen', Boolean(tg?.isFullscreen));
  }));
  safely(() => tg!.onEvent?.('fullscreenFailed', () => {
    document.documentElement.classList.remove('tg-fullscreen');
  }));
}

/**
 * Показать кнопку «На главный экран», если клиент умеет и ярлык не добавлен.
 * cb вызывается c true, когда кнопку стоит показать.
 */
export function canAddToHomeScreen(cb: (can: boolean) => void): void {
  if (!inTelegram || !tg?.addToHomeScreen || !tg.isVersionAtLeast?.('8.0')) return cb(false);
  if (!tg.checkHomeScreenStatus) return cb(true);
  try {
    tg.checkHomeScreenStatus((status) => cb(status === 'missed' || status === 'unknown'));
  } catch {
    cb(false);
  }
}

export function addToHomeScreen(): void {
  safely(() => tg?.addToHomeScreen?.());
}

export function initTelegram(): void {
  safely(() => tg?.ready?.());
  safely(() => tg?.expand?.());
  requestFullscreenSafe();
  applyThemeColors();
  safely(() => tg?.onEvent?.('themeChanged', applyThemeColors));
  if (inTelegram) document.documentElement.classList.add('telegram-app');
}

let backHandler: (() => void) | null = null;

export function showBackButton(onClick: () => void): void {
  if (!inTelegram || !tg?.BackButton) return;
  hideBackButton();
  backHandler = () => {
    haptic();
    onClick();
  };
  safely(() => tg.BackButton!.onClick(backHandler!));
  safely(() => tg.BackButton!.show());
}

export function hideBackButton(): void {
  if (!tg?.BackButton) return;
  if (backHandler) safely(() => tg.BackButton!.offClick(backHandler!));
  backHandler = null;
  safely(() => tg.BackButton!.hide());
}

let mainHandler: (() => void) | null = null;

/** Латунная MainButton (per-screen). В браузере — no-op, экран рисует свою кнопку. */
export function showMainButton(text: string, onClick: () => void): void {
  if (!inTelegram || !tg?.MainButton) return;
  hideMainButton();
  mainHandler = () => {
    haptic('medium');
    onClick();
  };
  safely(() =>
    tg.MainButton!.setParams({
      text,
      color: '#c8a24a',
      text_color: '#1a1408',
      is_active: true,
      is_visible: true,
    }),
  );
  safely(() => tg.MainButton!.onClick(mainHandler!));
  safely(() => tg.MainButton!.show());
}

export function hideMainButton(): void {
  if (!tg?.MainButton) return;
  if (mainHandler) safely(() => tg.MainButton!.offClick(mainHandler!));
  mainHandler = null;
  safely(() => tg.MainButton!.hide());
}

/** Прямой доступ к WebApp для узких кейсов (напр. tg user id в аналитике). */
export function getTg(): TgWebApp | undefined {
  return tg;
}
